import { toast } from "sonner";
import { Copy } from "lucide-react";
import type { EntityDetail } from "#shared/schemas/entityDetail.ts";
import type { FieldDef } from "#shared/schemas/fieldDef.ts";
import { Button } from "@web/components/ui/button.tsx";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@web/components/ui/context-menu.tsx";
import {
  buildExportRows,
  exportFilename,
  serializeExport,
  type ExportFormat,
} from "./exportDetail.ts";

async function writeClipboard(text: string, ok: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(ok);
  } catch {
    toast.error("Could not copy");
  }
}

function downloadText(filename: string, body: string) {
  const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Click = readable text dump. Right-click = JSON / CSV / download. */
export function SheetCopyButton({
  detail,
  defs,
}: {
  detail: EntityDetail;
  defs: readonly FieldDef[];
}) {
  const rows = buildExportRows(detail, defs);

  function copyFormat(format: ExportFormat, ok: string) {
    void writeClipboard(serializeExport(detail, rows, format), ok);
  }

  function download(format: ExportFormat) {
    const body = serializeExport(detail, rows, format);
    downloadText(exportFilename(detail, format), body);
    toast.success("Download started");
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="gap-1.5"
          onClick={() => copyFormat("text", "Copied")}
        >
          <Copy className="size-3.5" />
          Copy
        </Button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={() => copyFormat("text", "Copied as text")}>
          Copy as text
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => copyFormat("json", "Copied as JSON")}>
          Copy as JSON
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => copyFormat("csv", "Copied as CSV")}>
          Copy as CSV
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => download("text")}>Download text</ContextMenuItem>
        <ContextMenuItem onSelect={() => download("json")}>Download JSON</ContextMenuItem>
        <ContextMenuItem onSelect={() => download("csv")}>Download CSV</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
